import { doc, getDoc, Timestamp } from "firebase/firestore";
import Joi from "joi";
import { Clients } from "src/contexts/AppContext";
import {
  CALENDAR_PROVIDERS,
  parseVocationId,
  TRADE_QUESTION_TYPES,
  Vocation,
  VOCATION_APPOINTMENT_TYPES,
  VOCATION_ENTRY_REQUIREMENTS,
  VOCATION_ID_PATTERN,
  VOCATION_MOTIVATOR_SETS,
  VOCATION_SURVEY_STEPS,
  VOCATION_WIZARD_SEGMENTS,
} from "src/types/Vocation";
import { WIZARD_DOC_REQUIREMENT_IDS } from "../../../../shared-utils/complianceConstants";

type StoredVocation = Omit<Vocation, "id"> & { id: string };

// This mirrors functions/types/CDLVocationConfig.ts. Objects allow unknown
// fields because the Zod readers on the backend accept (and strip) future CMS
// fields rather than making existing documents unreadable.
const manualSectionSchema = Joi.object({
  sectionNumber: Joi.string().min(1).required(),
  name: Joi.string().min(1).required(),
}).unknown(true);

const eldtSectionRefSchema = Joi.object({
  code: Joi.string().min(1).required(),
  name: Joi.string().min(1).required(),
}).unknown(true);

const cdlCategorySchema = Joi.object({
  id: Joi.string().min(1).required(),
  name: Joi.string().min(1).required(),
  examSize: Joi.number().integer().min(1).required(),
  manualSections: Joi.array().items(manualSectionSchema).required(),
  eldtSections: Joi.array().items(eldtSectionRefSchema).required(),
}).unknown(true);

const eldtTopicSchema = Joi.object({
  code: Joi.string().min(1).required(),
  name: Joi.string().min(1).required(),
  sectionCode: Joi.string().min(1).required(),
  sectionName: Joi.string().min(1).required(),
}).unknown(true);

const tprConfigSchema = Joi.object({
  minVideosStarted: Joi.number().integer().min(0).required(),
  minAverageScore: Joi.number().min(0).max(1).required(),
  requiredCategories: Joi.array().items(Joi.string()).required(),
}).unknown(true);

const cdlVocationConfigSchema = Joi.object({
  categories: Joi.array().items(cdlCategorySchema).required(),
  eldtTopics: Joi.array().items(eldtTopicSchema).required(),
  tprConfig: tprConfigSchema.required(),
}).unknown(true);

// Curriculum. Mirrors functions/types/VocationCurriculumConfig.ts. Nested
// objects allow unknown fields so future additions don't make existing vocation
// documents unreadable (the backend Zod reader is the strict source of truth).
const curriculumQuizRefSchema = Joi.object({
  id: Joi.string().min(1).required(),
  name: Joi.string().min(1).required(),
  quizType: Joi.string().min(1).required(),
}).unknown(true);

const curriculumLessonSchema = Joi.object({
  id: Joi.string().min(1).required(),
  title: Joi.string().min(1).required(),
  order: Joi.number().integer().min(0).required(),
  muxPlaybackId: Joi.string().min(1).required(),
  duration: Joi.number().integer().min(0).required(),
  thumbnailUrl: Joi.string().min(1).optional(),
  quiz: curriculumQuizRefSchema.optional(),
  type: Joi.string().valid("video", "practice", "trailer").required(),
  // practice-only
  studyGuideId: Joi.when("type", {
    is: "practice",
    then: Joi.string().min(1).required(),
    otherwise: Joi.forbidden(),
  }),
  // video-only (optional; tolerated but not required on other types)
  fallbackUrl: Joi.string().min(1).optional(),
  description: Joi.string().allow("").optional(),
  summary: Joi.any().optional(),
}).unknown(true);

const curriculumUnitSchema = Joi.object({
  id: Joi.string().min(1).required(),
  name: Joi.string().min(1).required(),
  order: Joi.number().integer().min(0).required(),
  thumbnailUrl: Joi.string().min(1).optional(),
  lessons: Joi.array().items(curriculumLessonSchema).required(),
}).unknown(true);

const curriculumSchema = Joi.object({
  sourceCourseId: Joi.string().min(1).optional(),
  units: Joi.array().items(curriculumUnitSchema).required(),
}).unknown(true);

const tradeQuestionSchema = Joi.object({
  key: Joi.string().min(1).required(),
  label: Joi.string().min(1).required(),
  section: Joi.string().min(1).optional(),
  description: Joi.string().min(1).optional(),
  applicableStates: Joi.array()
    .items(Joi.string().pattern(/^[A-Z]{2}$/))
    .min(1)
    .unique()
    .optional(),
  type: Joi.string()
    .valid(...TRADE_QUESTION_TYPES)
    .required(),
  options: Joi.when("type", {
    is: "select",
    then: Joi.array().items(Joi.string().min(1)).min(1).unique().required(),
    otherwise: Joi.forbidden(),
  }),
  required: Joi.boolean().required(),
}).unknown(true);

const applicationSchema = Joi.object({
  steps: Joi.array()
    .items(Joi.string().valid(...VOCATION_SURVEY_STEPS))
    .unique()
    .required(),
  tradeQuestions: Joi.array()
    .items(tradeQuestionSchema)
    .unique("key")
    .default([]),
  autoDecision: Joi.boolean().required(),
  copyOverrides: Joi.object()
    .pattern(Joi.string(), Joi.string().allow(""))
    .default({}),
  interviewQuestions: Joi.object()
    .pattern(Joi.string(), Joi.string().allow(""))
    .optional(),
})
  .custom((application, helpers) => {
    const requiredCoreSteps = [
      "personal",
      "financial",
      "driving",
      "criminal",
      "medical",
    ];
    const steps = new Set<string>(application.steps);
    const missingCoreStep = requiredCoreSteps.find((step) => !steps.has(step));
    if (missingCoreStep) {
      return helpers.error("application.missingCoreStep", {
        step: missingCoreStep,
      });
    }

    const hasCdlVocational = steps.has("cdl_vocational");
    const hasTradeQuestions = steps.has("trade_questions");
    if (hasCdlVocational === hasTradeQuestions) {
      return helpers.error("application.incompatibleSections");
    }
    if (hasCdlVocational && application.tradeQuestions.length > 0) {
      return helpers.error("application.cdlTradeQuestions");
    }
    if (hasTradeQuestions && application.tradeQuestions.length === 0) {
      return helpers.error("application.emptyTradeQuestions");
    }

    return application;
  })
  .messages({
    "application.missingCoreStep":
      "application.steps is missing required step {{#step}}",
    "application.incompatibleSections":
      "application.steps must include exactly one vocation-specific section",
    "application.cdlTradeQuestions":
      "cdl_vocational applications cannot define trade questions",
    "application.emptyTradeQuestions":
      "trade_questions applications require at least one trade question",
  })
  .unknown(true);

const appointmentSchema = Joi.object({
  type: Joi.string()
    .valid(...VOCATION_APPOINTMENT_TYPES)
    .required(),
  calendarProvider: Joi.string()
    .valid(...CALENDAR_PROVIDERS)
    .required(),
  name: Joi.string().min(1).required(),
  slug: Joi.string().min(1).required(),
  path: Joi.string().min(1).required(),
}).unknown(true);

const enrollmentSchema = Joi.object({
  requiredDocs: Joi.array()
    .items(Joi.string().valid(...WIZARD_DOC_REQUIREMENT_IDS))
    .required(),
  wizardSegments: Joi.array()
    .items(Joi.string().valid(...VOCATION_WIZARD_SEGMENTS))
    .min(1)
    .unique()
    .required(),
  entryRequirement: Joi.string()
    .valid(...VOCATION_ENTRY_REQUIREMENTS)
    .required(),
  motivatorSet: Joi.string()
    .valid(...VOCATION_MOTIVATOR_SETS)
    .default("generic"),
  appointments: Joi.array().items(appointmentSchema).unique("type").default([]),
  copyOverrides: Joi.object()
    .pattern(Joi.string(), Joi.string().allow(""))
    .default({}),
})
  .custom((enrollment, helpers) => {
    const segments = new Set<string>(enrollment.wizardSegments);
    const hasDocumentsSegment = segments.has("documents");
    if (hasDocumentsSegment !== enrollment.requiredDocs.length > 0) {
      return helpers.error("enrollment.documentsMismatch");
    }

    if (
      segments.has("schedule_meetings") &&
      !enrollment.appointments.some(
        (appointment: { type: string }) =>
          appointment.type === "enrollment-interview"
      )
    ) {
      return helpers.error("enrollment.missingInterviewAppointment");
    }

    return enrollment;
  })
  .messages({
    "enrollment.documentsMismatch":
      "enrollment documents segment and requiredDocs must be configured together",
    "enrollment.missingInterviewAppointment":
      "schedule_meetings requires an enrollment-interview appointment",
  })
  .unknown(true);

/**
 * Built on first validate, not at module load. `Joi.instance()` asserts its
 * argument is a constructor, and `Timestamp` is undefined in any test that
 * partially mocks `firebase/firestore`. At module scope that threw while the
 * module was being imported, so every suite that merely reaches this file
 * through an import chain failed to run, even ones that never fetch a
 * vocation. Deferring it means only a test that actually validates a document
 * needs `Timestamp` in its mock.
 */
let cachedVocationSchema: Joi.ObjectSchema<StoredVocation> | undefined;

function vocationSchema(): Joi.ObjectSchema<StoredVocation> {
  if (cachedVocationSchema) return cachedVocationSchema;

  const timestampSchema = Joi.object().instance(Timestamp).required();

  cachedVocationSchema = Joi.object<StoredVocation>({
    id: Joi.string().pattern(VOCATION_ID_PATTERN).required(),
    name: Joi.string().min(1).required(),
    shortName: Joi.string().min(1).required(),
    description: Joi.string().allow("").optional(),
    isActive: Joi.boolean().required(),
    config: cdlVocationConfigSchema.optional(),
    curriculum: curriculumSchema.optional(),
    application: applicationSchema.optional(),
    enrollment: enrollmentSchema.optional(),
    createdAt: Joi.string().isoDate().required(),
    createdAtFirestoreTimestamp: timestampSchema,
    lastUpdatedAt: Joi.string().isoDate().required(),
    lastUpdatedAtFirestoreTimestamp: timestampSchema,
  }).unknown(true);

  return cachedVocationSchema;
}

/** Loads and validates one CMS-like vocation document. */
export default async function fetchVocation(
  vocationId: string,
  { db }: Clients
): Promise<Vocation | null> {
  const parsedVocationId = parseVocationId(vocationId);
  if (parsedVocationId === undefined) {
    throw new Error(`Invalid vocation id: ${vocationId}`);
  }

  const snapshot = await getDoc(doc(db, "vocations", parsedVocationId));
  if (!snapshot.exists()) return null;

  const result = vocationSchema().validate(
    { ...snapshot.data(), id: snapshot.id },
    { abortEarly: false }
  );
  if (result.error) {
    throw new Error(`Invalid vocation document: ${parsedVocationId}`);
  }

  return { ...result.value, id: parsedVocationId };
}
